export type OmieEmpresaChave = "corisco" | "faisca" | "gota_serena";
/** Same values as BillingArrangement in the API. */
export type ArranjoFaturamento = "repasse_liquido" | "taxa_e_comissao" | "total_e_comissao";

export type OmieTipoCadastro = "agencia" | "freelancer";

export type OmieVinculoFiltro = "a_conferir" | "pendente" | "sugerido" | "confirmado" | "todos";

export type OmieOrigemSugestao = "email" | "cnpj" | "nome";

export type OmieOrigemVinculo = "manual" | "email" | "cnpj" | "nome_confirmado" | "inferido" | "cadastro_plataforma";

export interface OmieEmpresaStatus {
  chave: OmieEmpresaChave;
  nome: string;
  credenciais_configuradas: boolean;
  ultimo_sync: string | null;
  total_clientes: number;
  vinculados: number;
}

export interface OmieStatusResponse {
  empresas: OmieEmpresaStatus[];
}

export interface OmieSyncResultadoEmpresa {
  chave: OmieEmpresaChave;
  total: number;
  novos: number;
  atualizados: number;
}

export interface OmieSyncResponse {
  empresas: OmieSyncResultadoEmpresa[];
  sugestoes_novas: number;
}

export interface OmieClienteVinculo {
  agencia_id: string | null;
  freelancer_id: string | null;
  nome: string;
  origem: OmieOrigemVinculo;
  vinculado_em: string;
  detalhe: string | null;
  conferido: boolean;
}

export interface OmieClienteSugestao {
  agencia_id: string | null;
  freelancer_id: string | null;
  nome: string;
  origem: OmieOrigemSugestao;
  score: number;
}

export interface OmieCliente {
  id: string;
  omie_empresa: { chave: OmieEmpresaChave; nome: string };
  codigo_cliente_omie: number;
  razao_social: string;
  nome_fantasia: string | null;
  cnpj_cpf: string | null;
  email: string | null;
  inativo: boolean;
  synced_at: string;
  vinculo: OmieClienteVinculo | null;
  sugestao: OmieClienteSugestao | null;
  observacao: string | null;
}

export interface OmieClientesResumo {
  total: number;
  confirmados: number;
  sugeridos: number;
  pendentes: number;
  a_conferir: number;
}

export interface OmieClientesResponse {
  items: OmieCliente[];
  total: number;
  page: number;
  page_size: number;
  resumo: OmieClientesResumo;
}

export interface OmieCandidato {
  id: string;
  nome: string;
  email: string | null;
  cnpj: string | null;
}

export interface OmieCandidatosResponse {
  items: OmieCandidato[];
}

export interface OmieVinculoBody {
  agencia_id?: string;
  freelancer_id?: string;
  origem: OmieOrigemVinculo;
}

export type OmieCampoCopiavel = "razao_social" | "cnpj" | "email" | "contato_email";

export interface OmieCopiarParaCadastroBody {
  campos: OmieCampoCopiavel[];
  sobrescrever?: boolean;
}

export interface OmieCopiarParaCadastroResposta {
  atualizados: string[];
  ignorados: { campo: string; motivo: string }[];
}

export interface OmieCompetencia {
  competencia: string;
  codigos: Partial<Record<OmieEmpresaChave, number | null>>;
}

export interface OmieCompetenciasResponse {
  empresas: { chave: OmieEmpresaChave; nome: string }[];
  items: OmieCompetencia[];
}

export interface OmieCompetenciaBody {
  codigos: Partial<Record<OmieEmpresaChave, number | null>>;
}

// ---------------------------------------------------------------------------
// Propostas (COR-383 Fase 2)
// ---------------------------------------------------------------------------

export type PropostaStatus =
  | "rascunho"
  | "gerada"
  | "enviada"
  | "aceita"
  | "lancada_omie"
  | "recusada"
  | "cancelada";

export type PropostaOrigem = "vaga" | "renovacao" | "avulsa";

export interface ProjetoElegivelFreelancer {
  id: string;
  nome: string;
}

export interface ProjetoElegivelVaga {
  id: string;
  descricao_curta: string;
}

export interface ProjetoElegivelPropostaExistente {
  id: string;
  codigo: string | null;
  status: PropostaStatus;
}

export interface OsPossivel {
  numero_os: string | null;
  periodo_inicio: string;
  periodo_fim: string | null;
  valor_cents: number | null;
  faturada: boolean;
}

export interface ProjetoElegivel {
  controle_projeto_id: string;
  freelancer: ProjetoElegivelFreelancer;
  area_atuacao: string | null;
  status: string;
  data_inicio: string | null;
  data_conclusao: string | null;
  valor_profissional_cents: number | null;
  moeda?: string | null;
  vaga: ProjetoElegivelVaga | null;
  proposta_existente: ProjetoElegivelPropostaExistente | null;
  tem_os_omie: boolean;
  /** Terminou antes do histórico de OS do Omie: nunca vai casar com uma OS, fica fora da lista. */
  finalizado_antes_do_omie: boolean;
  /** OS sem projeto que podem ser deste período: pede conferência, não bloqueia. */
  os_possiveis: OsPossivel[];
}

export interface ProjetosElegiveisResponse {
  items: ProjetoElegivel[];
}

export interface PropostaPorProjetoItem {
  id: string;
  codigo: string | null;
  status: PropostaStatus;
}

export interface PropostasPorProjetoResponse {
  items: Record<string, PropostaPorProjetoItem>;
}

export interface CreatePropostaBody {
  agencia_id: string;
  controle_projeto_ids: string[];
  origem?: PropostaOrigem;
  vaga_id?: string;
}

export interface PropostaAgenciaResumo {
  id: string;
  nome: string;
  sigla: string | null;
}

export interface PropostaDocumentoAtual {
  versao: number;
  gerado_em: string;
  pdf_pendente: boolean;
}

export interface PropostaTotais {
  profissional_cents: number;
  plataforma_cents: number;
  impostos_cents: number;
  total_cents: number;
}

export interface PropostaResumoPeriodo {
  freelancer_id: string;
  freelancer_nome: string;
  data_inicio: string | null;
  data_conclusao: string | null;
}

export interface PropostaResumo {
  id: string;
  codigo: string | null;
  agencia: PropostaAgenciaResumo;
  status: PropostaStatus;
  competencia: string | null;
  moeda?: string | null;
  taxa_percentual: number;
  totais: PropostaTotais;
  itens_count: number;
  freelancers_count: number;
  periodos: PropostaResumoPeriodo[];
  documento_atual: PropostaDocumentoAtual | null;
  pendencias_bloqueantes: number;
  created_at: string;
  updated_at: string;
}

export interface PropostasListResponse {
  items: PropostaResumo[];
  total: number;
  page: number;
  page_size: number;
}

export interface PropostaTextos {
  introducao: string;
  como_funciona: string;
  investimentos: string;
  pagamento: string;
  aceite: string;
}

export interface PropostaAgenciaDetail {
  id: string;
  nome: string;
  sigla: string | null;
  razao_social: string | null;
  cnpj: string | null;
  endereco: string | null;
  contato_nome: string | null;
  contato_email: string | null;
  contato_cargo: string | null;
  fatura_por_competencia_mensal: boolean;
  dia_emissao_competencia: number | null;
  prazo_pagamento_competencia_dias: number | null;
  impostos_percentual: number | null;
  /** Arranjo salvo na agência: de onde as propostas novas partem. */
  faturamento_empresa: OmieEmpresaChave | null;
  faturamento_arranjo: ArranjoFaturamento | null;
  omie_vinculado: boolean;
}

/** Uma nota do cronograma do invite. Datas em ISO. */
export interface ParcelaDoInvite {
  inicio: string;
  fim: string;
  diarias: number;
  valor_cents: number;
  emissao: string;
  pagamento: string;
  repasse_cents: number;
  repasse_previsto: string;
}

export interface PreviaDoInvite {
  /** A linha "Recebimento" do período: a orientação digitada ou a previsão automática. */
  recebimento: string | null;
  parcelas: ParcelaDoInvite[];
  /** Com a comissão retida: a nota do profissional, no valor líquido, e o pagamento a ele. */
  pagamentos_freelancer: PagamentoDoProfissional[];
}

export interface PagamentoDoProfissional {
  inicio: string;
  fim: string;
  diarias: number;
  valor_cents: number;
  envio_nf: string;
  pagamento: string;
}

export interface PropostaItem {
  id: string;
  ordem: number | null;
  controle_projeto_id: string;
  data_inicio: string | null;
  data_conclusao: string | null;
  diarias: number | null;
  valor_profissional_cents: number | null;
  valor_plataforma_cents: number | null;
  valor_plataforma_manual: boolean;
  valor_impostos_cents: number | null;
  total_cents: number | null;
  /** Só a data digitada; vazia, vale `data_faturamento_prevista`. */
  data_faturamento: string | null;
  data_recebimento: string | null;
  /** Fim do período + 7 dias, quando a data de faturamento está vazia. */
  data_faturamento_prevista: string | null;
  /** Faturamento (digitado ou previsto) + prazo da agência, quando a data de recebimento está vazia. */
  data_recebimento_prevista: string | null;
  data_repasse: string | null;
  data_repasse_manual: boolean;
  /** Dias úteis entre o pagamento e a Taxa Faísca, os da proposta. */
  taxa_dias_uteis: number;
  /** Com a comissão retida: quando a plataforma paga o profissional (digitada ou término + 30 dias). */
  data_pagamento_freelancer: string | null;
  data_pagamento_freelancer_manual: boolean;
  /** Com a comissão retida: até quando o profissional envia a nota (digitada ou pagamento - 7 dias). */
  data_nf_freelancer: string | null;
  data_nf_freelancer_manual: boolean;
  recebimento_texto: string | null;
  /** O que o invite imprime para este período. */
  previa_invite: PreviaDoInvite | null;
  /** When the team confirmed the receipt text (required when the agency has a registered text). */
  recebimento_confirmado_em: string | null;
  /** Courtesy notice shown in the invite for this period. */
  aviso_cortesia: string | null;
  comissao_percentual: number;
  comissao_cents: number | null;
  empresa_agencia: EmpresaFaturamento | null;
  empresa_freelancer: EmpresaFaturamento | null;
  modo_agencia: "taxa" | "total";
  modo_freelancer: "cobrar" | "reter";
  /** Vaga fixa: regime da contratação. */
  tipo_contrato: RegimeVagaFixa | null;
  /** Vaga fixa: desconto sobre a taxa de tabela da agência. */
  desconto_percentual: number;
  /** Vaga fixa: desconto sobre a taxa de tabela do profissional. */
  desconto_comissao_percentual: number;
}

export type ModalidadeProposta = "frila" | "fixa";
export type RegimeVagaFixa = "clt" | "pj";

export interface EmpresaFaturamento {
  id: string;
  chave: OmieEmpresaChave;
  nome: string;
  modelo: "taxa" | "comissao" | "total_repasse";
  lado_agencia: boolean;
  lado_freelancer: boolean;
}

export interface PropostaInviteDocumento {
  versao: number;
  gerado_em: string;
  gerado_por_nome: string | null;
  tem_pdf: boolean;
  tem_html: boolean;
  pdf_pendente: boolean;
  origem: string;
}

export type InviteStatus = "gerado" | "enviado" | "aceito" | "recusado" | "cancelado";

export interface PropostaInvite {
  id: string;
  freelancer_id: string;
  freelancer_nome: string | null;
  codigo: string | null;
  status: InviteStatus;
  documentos: PropostaInviteDocumento[];
  dados_alterados_em: string | null;
  cadastro_mudou: boolean;
}

/** Passo do ciclo que a equipe marca na proposta ou num invite. */
export type AcaoDoCiclo = "enviado" | "aceito" | "recusado" | "reaberto";

export interface CicloBody {
  data?: string | null;
  para?: string[];
  aceito_por?: string | null;
  motivo?: string | null;
  observacao?: string | null;
}

export interface PropostaEvento {
  id: string;
  invite_id: string | null;
  freelancer_id: string | null;
  freelancer_nome: string | null;
  acao: AcaoDoCiclo | "cancelado" | "enviado_omie" | "cotacao_omie";
  status_anterior: string;
  status_novo: string;
  ocorrido_em: string;
  registrado_em: string;
  registrado_por_nome: string | null;
  para: string[];
  aceito_por: string | null;
  motivo: string | null;
  observacao: string | null;
  versao: number | null;
}

export interface PropostaGrupoFreelancer {
  id: string;
  nome: string;
  email: string | null;
  omie_vinculado: boolean;
  cnpj?: string | null;
  documento_omie?: string | null;
  omie_cliente_id?: string | null;
}

export interface PropostaFreelancerDocumentos {
  cnpj: string | null;
  documento_omie: string | null;
  omie_cliente_id: string | null;
}

export interface PatchFreelancerDaPropostaBody {
  cnpj: string | null;
}

export interface PropostaGrupo {
  freelancer: PropostaGrupoFreelancer;
  cargo: string | null;
  comissao_percentual: number;
  periodo_inicial: number;
  itens: PropostaItem[];
}

export interface PropostaPendencia {
  codigo: string;
  mensagem: string;
  bloqueia_geracao: boolean;
  item_id: string | null;
  campo: string | null;
  link: string | null;
}

export interface PropostaDocumentoVersao {
  versao: number;
  gerado_em: string;
  gerado_por_nome: string | null;
  tem_pdf: boolean;
  tem_html: boolean;
  pdf_pendente: boolean;
  origem: string | null;
}

export interface PropostaTotaisDetail extends PropostaTotais {
  comissao_faisca_cents: number;
}

export interface PropostaVagaResumo {
  id: string;
  descricao_curta: string | null;
}

export interface PropostaOmieEmpresaResumo {
  chave: string;
  nome: string;
}

/** Companies billing this proposal that have no Omie project code for the competency. */
export interface CompetenciaSemCodigo {
  competencia: string;
  empresas: Array<{ chave: string; nome: string }>;
  mensagem: string;
}

export interface PropostaDetail extends PropostaResumo {
  /** Reais por unidade da moeda da proposta: as OS saem em reais por ela. */
  cotacao_omie: number | null;
  cotacao_omie_em: string | null;
  /** Referência para quem preenche a cotação (Plataforma > Parâmetros). */
  cotacao_sugerida: number | null;
  competencia_sem_codigo: CompetenciaSemCodigo | null;
  /** The agency has a registered receipt text: every period needs "Confirmar texto". */
  recebimento_confirmacao_exigida: boolean;
  modalidade: ModalidadeProposta;
  origem: PropostaOrigem | null;
  vaga: PropostaVagaResumo | null;
  modelo: string;
  omie_empresa_agencia: PropostaOmieEmpresaResumo | null;
  ac_contato: string | null;
  validade_dias: number;
  observacoes: string | null;
  textos: PropostaTextos;
  codigo_previsto: string | null;
  /** Uma OS está ou pode estar no Omie (enviada, enviando, ou erro sem resposta): o código não muda mais. */
  os_enviada_ao_omie: boolean;
  prazo_recebimento_dias: number | null;
  modelos: string[];
  empresas: EmpresaFaturamento[];
  invites: PropostaInvite[];
  agencia: PropostaAgenciaDetail;
  grupos: PropostaGrupo[];
  totais: PropostaTotaisDetail;
  pendencias: PropostaPendencia[];
  documentos: PropostaDocumentoVersao[];
  gerada_em: string | null;
  cancelada_em: string | null;
  motivo_cancelamento: string | null;
  enviada_em: string | null;
  enviada_para: string[];
  aceita_em: string | null;
  aceita_por: string | null;
  recusada_em: string | null;
  motivo_recusa: string | null;
  eventos: PropostaEvento[];
  /** Última mudança nos dados, sem contar envio, aceite e recusa. */
  dados_alterados_em: string | null;
  cadastro_mudou: boolean;
  modelo_documento: ModeloDoDocumentoDaProposta;
  fatura_por_competencia_mensal: FaturaPorCompetenciaMensal;
  taxa_dias_uteis: number;
  impostos_percentual: ImpostosDaProposta;
}

/** Impostos sobre profissional + plataforma, à parte na proposta (a Netflix pede). */
export interface ImpostosDaProposta {
  /** Escolhido na proposta; null = segue a agência, 0 = sem impostos. */
  escolhido: number | null;
  /** null = a agência não tem impostos. */
  padrao_agencia: number | null;
  em_uso: number;
}

/** Invite com uma nota por mês do período (competência mensal) ou uma por período. */
export interface FaturaPorCompetenciaMensal {
  /** Escolhido na proposta; null = segue a agência. */
  escolhido: boolean | null;
  padrao_agencia: boolean;
  em_uso: boolean;
  /** Dia do mês seguinte ao trabalhado em que a nota sai. */
  dia_emissao: PrazoDaCompetenciaMensal;
  /** Dias entre a nota e o pagamento da agência. */
  prazo_pagamento_dias: PrazoDaCompetenciaMensal;
}

/** Valor da competência mensal: a agência sugere, a proposta pode mudar. */
export interface PrazoDaCompetenciaMensal {
  /** Escolhido na proposta; null = segue a agência. */
  escolhido: number | null;
  padrao_agencia: number | null;
  em_uso: number | null;
}

/** Modelo de documento de uma empresa que fatura (proposta e invites). */
export type VarianteDoModelo = "corisco" | "faisca" | "gota_serena";

export interface ModeloDoDocumentoDaProposta {
  /** Escolhido na proposta; null = segue quem fatura a agência. */
  escolhido: VarianteDoModelo | null;
  automatico: VarianteDoModelo;
  em_uso: VarianteDoModelo;
  opcoes: Array<{ id: VarianteDoModelo; rotulo: string }>;
  trechos_editados: number;
}

/** Documento que se edita direto na prévia: a proposta ou o invite de um freelancer. */
export type DocumentoEditavel = { tipo: "proposta" } | { tipo: "invite"; freelancerId: string };

export interface EdicaoDeTrecho {
  titulo?: string;
  html?: string;
  removida?: boolean;
}

export interface PatchPropostaItemBody {
  id: string;
  empresa_agencia?: OmieEmpresaChave;
  empresa_freelancer?: OmieEmpresaChave;
  modo_agencia?: "taxa" | "total";
  modo_freelancer?: "cobrar" | "reter";
  cargo?: string;
  valor_plataforma_cents?: number | null;
  data_faturamento?: string | null;
  data_recebimento?: string | null;
  data_repasse?: string | null;
  data_pagamento_freelancer?: string | null;
  data_nf_freelancer?: string | null;
  recebimento_texto?: string | null;
  recebimento_confirmado?: boolean;
  aviso_cortesia?: string | null;
  diarias?: number | null;
  comissao_percentual?: number | null;
  valor_profissional_cents?: number;
  data_inicio?: string;
  data_conclusao?: string;
  tipo_contrato?: RegimeVagaFixa;
  periodo_inicial?: number;
  desconto_percentual?: number;
  desconto_comissao_percentual?: number;
}

export interface DisponibilidadeCodigoProposta {
  codigo: string | null;
  disponivel: boolean;
  mensagem: string | null;
}

export interface PatchPropostaBody {
  modalidade?: ModalidadeProposta;
  taxa_percentual?: number;
  ac_contato?: string | null;
  competencia?: string | null;
  validade_dias?: number;
  observacoes?: string | null;
  textos?: Partial<Record<keyof PropostaTextos, string | null>>;
  modelo_documento?: VarianteDoModelo | null;
  fatura_por_competencia_mensal?: boolean | null;
  dia_emissao_competencia?: number | null;
  prazo_pagamento_competencia_dias?: number | null;
  impostos_percentual?: number | null;
  taxa_dias_uteis?: number;
  itens?: PatchPropostaItemBody[];
  codigo?: string | null;
}

/** Standard invite receipt text: for one agency, one billing company, or general. */
export interface InviteReceiptText {
  id: string;
  titulo: string;
  texto: string;
  agencia: { id: string; nome: string } | null;
  empresa: { chave: OmieEmpresaChave; nome: string } | null;
  ativo: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface InviteReceiptTextsResponse {
  items: InviteReceiptText[];
}

export interface InviteReceiptTextBody {
  titulo: string;
  texto: string;
  agencia_id: string | null;
  omie_empresa: OmieEmpresaChave | null;
}

export interface InviteReceiptTextPatch extends Partial<InviteReceiptTextBody> {
  ativo?: boolean;
}

/** Period with no project yet: the API creates a draft project for it. */
export interface NovoPeriodoBody {
  freelancer_id: string;
  data_inicio: string;
  data_conclusao: string;
  valor_profissional_cents: number;
  cargo?: string | null;
}

export interface PatchAgenciaDaPropostaBody {
  sigla?: string | null;
  razao_social?: string | null;
  cnpj?: string | null;
  endereco?: string | null;
  contato_nome?: string | null;
  contato_email?: string | null;
  contato_cargo?: string | null;
  fatura_por_competencia_mensal?: boolean;
  dia_emissao_competencia?: number | null;
  prazo_pagamento_competencia_dias?: number | null;
  impostos_percentual?: number | null;
  faturamento_empresa?: OmieEmpresaChave | null;
  faturamento_arranjo?: ArranjoFaturamento | null;
}

export interface OrdemServicoDestinatario {
  codigo_cliente_omie: number | null;
  nome: string;
  documento: string | null;
}

export type SituacaoOsChave = "aguardando_revisao" | "confirmada" | "faturada" | "cancelada";

export interface SituacaoOsOmie {
  chave: SituacaoOsChave;
  rotulo: string;
}

export interface OrdemServicoPeriodo {
  item_id: string;
  profissional: string;
  cargo: string;
  periodo: string;
  valor_cents: number | null;
  data_faturamento: string | null;
}

export interface OrdemServicoPrevia {
  empresa: OmieEmpresaChave;
  empresa_nome: string;
  tipo: "comissao_agencia" | "comissao_freelancer" | "total_repasse";
  codigo_integracao: string;
  destinatario: OrdemServicoDestinatario;
  profissional: string;
  cargo: string;
  periodo: string;
  data_faturamento: string | null;
  data_recebimento: string | null;
  valor_cents: number | null;
  descricao: string;
  observacao: string;
  payload: Record<string, unknown>;
  faltando: string[];
  linhas: Array<{ descricao: string; valor_cents: number | null; categoria: string | null }>;
  freelancer_id: string | null;
  lado: "agencia" | "freelancer" | null;
  momento_emissao: string | null;
  anexo: string | null;
  emissao_liberada: boolean;
  anexo_pronto: boolean;
  os_id: string | null;
  ordem: number | null;
  modo: string | null;
  itens: OrdemServicoPeriodo[];
  avisos: string[];
  situacao_omie: SituacaoOsOmie | null;
  envio: EnvioOsOmie | null;
}

export type EnvioOsStatus = "enviando" | "enviada" | "erro";

/** O que a plataforma enviou ao Omie para esta OS. */
export interface EnvioOsOmie {
  status: EnvioOsStatus;
  numero_os: string | null;
  erro: string | null;
  tentativas: number;
  enviada_em: string | null;
  /** A proposta mudou depois do envio: a OS no Omie ficou com os valores antigos. */
  mudou_depois: boolean;
}

export interface EnviarOsBody {
  codigos?: string[];
}

export interface EnviarOsResponse {
  enviadas: number;
  com_erro: number;
  ja_no_omie: string[];
  resultados: Array<{
    codigo_integracao: string;
    status: "enviada" | "erro";
    numero_os: string | null;
    erro: string | null;
  }>;
  previa: OrdensServicoPreviaResponse;
}

export type TotalPorEmpresa = { chave: OmieEmpresaChave; nome: string; total_cents: number };

export type AcaoDeAgrupamento = "mover" | "separar_todas" | "juntar_todas" | "sugerido";

export interface AgrupamentoOsBody {
  acao: AcaoDeAgrupamento;
  item_id?: string;
  os_id?: string | null;
}

export interface SincronizarOsResponse {
  empresas: Array<{ chave: OmieEmpresaChave; total: number; novas: number; atualizadas: number; ignoradas: number }>;
  sem_credenciais: string[];
}

export interface OrdensServicoPreviaResponse {
  moeda?: string | null;
  cotacao_omie?: number | null;
  cotacao_sugerida?: number | null;
  codigo: string | null;
  codigo_provisorio: boolean;
  competencia: string | null;
  envio_habilitado: boolean;
  cadastro_cliente_habilitado: boolean;
  agencia_id: string | null;
  status_proposta: PropostaStatus | null;
  itens: OrdemServicoPrevia[];
  total_corisco_cents: number;
  total_faisca_cents: number;
  totais_por_empresa: TotalPorEmpresa[];
  totais_por_lado: { agencia: TotalPorEmpresa[]; freelancer: TotalPorEmpresa[] };
}

export interface GenerateDocumentoResponse {
  versao: number;
  pdf_disponivel: boolean;
  html_disponivel: boolean;
  pdf_pendente: boolean;
  codigo: string | null;
  invites: GeneratedInvite[];
}

export interface GeneratedInvite {
  freelancer_id: string;
  invite_id: string;
  versao: number;
  pdf_disponivel: boolean;
}

export interface DocumentoDownloadResponse {
  url: string;
  expires_in: number;
}

export interface PropostaPreviewResponse {
  html: string;
}

export type FormatoDocumento = "pdf" | "html";

export interface ListarPropostasParams {
  agencia_id?: string;
  status?: PropostaStatus;
  competencia?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

// ---------------------------------------------------------------------------
// Modelos de documento (aba Templates)
// ---------------------------------------------------------------------------

export type TemplateTipo = "proposta" | "invite" | "proposta_fixa" | "invite_fixa";

export function ehModeloDeInvite(tipo: TemplateTipo): boolean {
  return tipo === "invite" || tipo === "invite_fixa";
}

export interface TemplateVersao {
  id: string;
  versao: number;
  ativo: boolean;
  observacao: string | null;
  autor: string | null;
  criado_em: string | null;
  tamanho: number | null;
}

export interface TemplateResumo {
  tipo: TemplateTipo;
  variante: VarianteDoModelo;
  em_uso: {
    id: string | null;
    versao: number | null;
    origem: "padrao" | "editado";
    hash: string | null;
  };
  padrao_hash: string | null;
  variaveis: string[];
  variaveis_desconhecidas: string[];
  versoes: TemplateVersao[];
}

export type ModeloDeFaturamento = "taxa" | "total";

export interface SecaoTexto {
  id: string;
  tipo: "texto";
  titulo: string | null;
  html: string;
  chave: string | null;
  mostrar_quando: ModeloDeFaturamento | null;
}

export interface SecaoBloco {
  id: string;
  tipo: "bloco";
  bloco: string;
}

export type SecaoDoModelo = SecaoTexto | SecaoBloco;

export interface ConteudoDoModelo {
  formato: "blocos" | "html";
  secoes: SecaoDoModelo[] | null;
  conteudo: string;
}

export interface CatalogoDoModelo {
  variaveis: Array<{ nome: string; rotulo: string; exemplo: string | null }>;
  blocos: Array<{ bloco: string; rotulo: string; descricao: string | null }>;
  chaves: Array<{ chave: string; rotulo: string }>;
  modelos: Array<{ id: ModeloDeFaturamento; rotulo: string }>;
  variantes: Array<{ id: VarianteDoModelo; rotulo: string }>;
}

// ---------------------------------------------------------------------------
// Histórico de OS do Omie (aba OS)
// ---------------------------------------------------------------------------

export type SituacaoOs = "faturada" | "a_faturar" | "cancelada";

export interface OsHistorico {
  id: string;
  empresa: OmieEmpresaChave;
  numero_os: string | null;
  cliente_nome: string | null;
  status: string | null;
  etapa: string | null;
  faturada: boolean;
  cancelada: boolean;
  data_previsao: string | null;
  data_faturamento: string | null;
  data_vencimento: string | null;
  valor_cents: number | null;
  competencia: string | null;
  codigo_proposta: string | null;
  profissional_nome: string | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  descricao: string | null;
  controle_projeto_id: string | null;
  codigo_integracao: string | null;
  sincronizada_em: string | null;
  fonte: string | null;
  situacao: SituacaoOsOmie | null;
  agencia: { id: string; nome: string | null } | null;
  freelancer: { id: string; nome: string | null } | null;
}

export interface OsHistoricoResponse {
  items: OsHistorico[];
  total: number;
  page: number;
  page_size: number;
  totais: { total_cents: number; faturado_cents: number; a_faturar_cents: number; cancelado_cents: number };
  totais_por_empresa: Array<{ chave: OmieEmpresaChave; total_cents: number; quantidade: number }>;
}

export interface ListarOsParams {
  empresa?: OmieEmpresaChave;
  situacao?: SituacaoOs;
  competencia?: string;
  agencia_id?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

/** Formulário do "Enviar ao Omie": vai ao IncluirCliente e fica no nosso cadastro. */
export interface ClienteOmieFormulario {
  razao_social: string | null;
  nome_fantasia: string | null;
  cnpj_cpf: string | null;
  email: string | null;
  telefone: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  codigo_ibge: string | null;
}

export interface RascunhoClienteOmie {
  tipo: OmieTipoCadastro;
  id: string;
  empresa: { chave: OmieEmpresaChave; nome: string };
  formulario: ClienteOmieFormulario;
  /** null quando não há documento; false quando os dígitos verificadores não batem. */
  documento_valido: boolean | null;
  vinculado: boolean;
  cadastro_habilitado: boolean;
}

export interface CadastrarClienteOmieBody {
  empresa: OmieEmpresaChave;
  formulario: ClienteOmieFormulario;
  ignorar_existentes?: boolean;
}

export interface ClienteOmieGuardado {
  id: string;
  codigo_cliente_omie: number;
  razao_social: string | null;
  nome_fantasia: string | null;
  cnpj_cpf: string | null;
  email: string | null;
  agencia_id: string | null;
  freelancer_id: string | null;
}

/** 409 do cadastro: o Omie já tem cliente com este CNPJ/CPF. */
export interface ClientesOmieExistentes {
  mensagem: string;
  clientes: ClienteOmieGuardado[];
}

export interface EnderecoDoCep {
  cep: string;
  logradouro: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  codigo_ibge: string | null;
}
